"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { userService } from "@/services/userService";
import type { CustomerWithDetails } from "@/types/customer.types";

const PAGE_SIZE = 20;

interface CustomerOption {
  id: string;
  name: string;
  phone: string | null;
}

interface CustomerPickerComboboxProps {
  /** Selected customer's User id, or "" when nothing is selected. */
  value: string;
  onChange: (customerId: string) => void;
}

function toOption(customer: CustomerWithDetails): CustomerOption {
  return {
    id: customer.id,
    name: customer.name || customer.email || "Unknown",
    phone: customer.phone ?? null,
  };
}

/** Appends a page of results, dropping any customer already listed. */
function mergeOptions(
  existing: CustomerOption[],
  incoming: CustomerOption[],
): CustomerOption[] {
  const seenIds = new Set(existing.map((option) => option.id));
  const fresh = incoming.filter((option) => !seenIds.has(option.id));
  return [...existing, ...fresh];
}

/**
 * Searchable, paginated single-customer picker for the reports page.
 *
 * Search and paging both happen server-side via /users/allcustomers, so the
 * page never has to load the whole customer base up front.
 */
export function CustomerPickerCombobox({
  value,
  onChange,
}: CustomerPickerComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Held separately from `options` so the trigger keeps showing the chosen
  // customer even once the result list has been replaced by a new search.
  const [selected, setSelected] = useState<CustomerOption | null>(null);

  // Guards against a slow early request overwriting a later one's results.
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(async (term: string, pageToLoad: number) => {
    const requestId = ++requestIdRef.current;
    const isFirstPage = pageToLoad === 1;

    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await userService.getCustomersPaginated({
        page: pageToLoad,
        limit: PAGE_SIZE,
        search: term.trim() || undefined,
        sortBy: "name",
        sortOrder: "asc",
      });
      if (requestId !== requestIdRef.current) {
        return;
      }

      const pageOptions = Array.isArray(response?.data)
        ? response.data.map(toOption)
        : [];

      if (isFirstPage) {
        setOptions(pageOptions);
      } else {
        setOptions((previous) => mergeOptions(previous, pageOptions));
      }
      setPage(pageToLoad);
      setTotalPages(response?.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching customers:", error);
      if (requestId === requestIdRef.current && isFirstPage) {
        setOptions([]);
        setTotalPages(1);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  // A new search term (or reopening) always restarts from page 1. Only query
  // while the popover is open -- no point loading a list nobody is looking at.
  useEffect(() => {
    if (open) {
      fetchPage(debouncedSearch, 1);
    }
  }, [open, debouncedSearch, fetchPage]);

  // Keep the label in step when the selection is cleared from outside.
  useEffect(() => {
    if (!value) {
      setSelected(null);
    }
  }, [value]);

  const handleSelect = (customer: CustomerOption) => {
    setSelected(customer);
    onChange(customer.id);
    setOpen(false);
  };

  const handleLoadMore = () => {
    fetchPage(debouncedSearch, page + 1);
  };

  const hasMore = page < totalPages;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span
            className={cn("truncate", !selected && "text-muted-foreground")}
          >
            {selected ? selected.name : "Select a customer..."}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        {/* Results come from the server already filtered; cmdk's own
            filtering would then hide valid matches. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search name, email or phone..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Searching...
              </div>
            ) : (
              <>
                <CommandEmpty>No customers found.</CommandEmpty>
                {options.length > 0 && (
                  <CommandGroup>
                    {options.map((customer) => (
                      <CommandItem
                        key={customer.id}
                        value={customer.id}
                        onSelect={() => handleSelect(customer)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === customer.id ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="flex-1 truncate">{customer.name}</span>
                        {customer.phone && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {customer.phone}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {hasMore && (
                  <div className="border-t p-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load more"
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
